<template>
  <div class="test-view" role="main" aria-labelledby="test-view-title">
    <h1 id="test-view-title">🧪 Test View</h1>

    <div class="test-content">
      <!-- Answer Box with Animation -->
      <transition name="fade-slide" appear>
        <div class="answer-box" role="region" aria-label="Answer display">
          <p class="label">
            {{ testData?.question || 'The Answer to Life, the Universe, and Everything' }}:
          </p>
          <p
            v-if="testData?.testColumn !== undefined"
            class="answer"
            aria-label="The test column value is {{ testData.testColumn }}"
          >
            🐭🐭 {{ testData.testColumn }} 🐬🐬
          </p>
          <p
            v-else-if="testData?.answer"
            class="answer"
            aria-label="The answer is {{ testData.answer }}"
          >
            🐭🐭 {{ testData.answer }} 🐬🐬
          </p>
          <p v-else-if="testDataLoading" class="answer">Loading...</p>
          <p v-else class="answer">No data available</p>
          <p v-if="testData?.description" class="reference-text">{{ testData.description }}</p>
          <p class="reference-text">🪓 Jacqui is coming for the dolphins</p>
        </div>
      </transition>

      <!-- Backend Health - Success State -->
      <transition name="fade-slide">
        <div
          v-if="backendHealth"
          class="info-box"
          role="status"
          aria-live="polite"
          aria-label="Backend connection status"
        >
          <div class="info-header">
            <i class="pi pi-check-circle" aria-hidden="true"></i>
            <h2>Backend Connected</h2>
          </div>
          <div class="info-content">
            <div class="info-item">
              <span class="info-label">Status:</span>
              <span class="info-value status-healthy">{{ backendHealth.status }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Version:</span>
              <span class="info-value">{{ backendHealth.version }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Environment:</span>
              <span class="info-value">{{ backendHealth.environment }}</span>
            </div>
            <p class="timestamp">Last updated: {{ formatTimestamp(backendHealth.timestamp) }}</p>
          </div>
        </div>
      </transition>

      <!-- Loading State with Skeleton -->
      <transition name="fade">
        <div
          v-if="loading && !backendHealth"
          class="loading-box"
          role="status"
          aria-live="polite"
          aria-label="Loading backend information"
        >
          <div class="loading-header">
            <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
            <p>Connecting to backend...</p>
          </div>
          <Skeleton height="6rem" border-radius="8px"></Skeleton>
        </div>
      </transition>

      <!-- Error State -->
      <transition name="fade">
        <div v-if="error && !backendHealth" class="error-box" role="alert" aria-live="assertive">
          <div class="error-header">
            <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
            <h2>Backend Connection Error</h2>
          </div>
          <p class="error-message">{{ error }}</p>
          <button
            @click="() => refetch()"
            class="retry-button"
            aria-label="Retry connection to backend"
          >
            <i class="pi pi-refresh" aria-hidden="true"></i>
            Retry Connection
          </button>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import Skeleton from 'primevue/skeleton';
import { executeQuery } from '../services/appsync-client.js';
import { GET_HEALTH } from '../graphql/getHealth.js';
import { GET_TEST_DATA } from '../graphql/getTestData.js';

const backendHealth = ref<any>(null);
const testData = ref<any>(null);
const loading = ref(true);
const testDataLoading = ref(true);
const error = ref<string | null>(null);

// Fetch health data
async function fetchHealth() {
  try {
    loading.value = true;
    error.value = null;
    const result = await executeQuery(GET_HEALTH);
    if (result?.health) {
      backendHealth.value = result.health;
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to fetch health data';
    console.error('Failed to fetch health:', err);
  } finally {
    loading.value = false;
  }
}

// Fetch test data
async function fetchTestData() {
  try {
    testDataLoading.value = true;
    const result = await executeQuery(GET_TEST_DATA);
    if (result?.testData) {
      testData.value = result.testData;
    }
  } catch (err) {
    console.error('Failed to fetch test data:', err);
    // Don't show error for testData - it's optional
  } finally {
    testDataLoading.value = false;
  }
}

// Refetch function for retry button
async function refetch() {
  await Promise.all([fetchHealth(), fetchTestData()]);
}

// Load data on mount
onMounted(() => {
  fetchHealth();
  fetchTestData();
});

// Format timestamp to human-readable format
const formatTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return timestamp;
  }
};
</script>

<style scoped>
.test-view {
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
}

h1 {
  color: #42b983;
  margin-bottom: 2rem;
  font-size: 2.5rem;
  font-weight: 700;
}

.test-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Answer Box - Enhanced with Shadow and Hover */
.answer-box {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 3rem;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
}

.answer-box:hover {
  transform: translateY(-4px);
  box-shadow: 0 15px 40px rgba(102, 126, 234, 0.5);
}

.label {
  font-size: 1.2rem;
  margin-bottom: 1rem;
  opacity: 0.95;
  font-weight: 500;
}

.answer {
  font-size: 5rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
  line-height: 1;
}

.reference-text {
  font-size: 1rem;
  margin-top: 1.5rem;
  opacity: 0.9;
  font-weight: 400;
  font-style: italic;
}

/* Info Box - Enhanced with Better Visual Hierarchy */
.info-box {
  background: linear-gradient(135deg, #f6f8fb 0%, #e5ebf1 100%);
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.2);
  transition: box-shadow 0.3s ease;
}

.info-box:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
}

.info-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.info-header i {
  color: #10b981;
  font-size: 1.5rem;
}

.info-header h2 {
  color: #10b981;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.info-label {
  color: #6b7280;
  font-weight: 500;
}

.info-value {
  color: #374151;
  font-weight: 600;
}

.status-healthy {
  color: #10b981;
  text-transform: capitalize;
}

.timestamp {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #9ca3af;
  font-style: italic;
  text-align: center;
}

/* Loading Box - Enhanced with Animation */
.loading-box {
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 2px dashed #3b82f6;
}

.loading-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.loading-header i {
  color: #3b82f6;
  font-size: 1.5rem;
}

.loading-header p {
  color: #3b82f6;
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0;
}

/* Error Box - Enhanced with Better UX */
.error-box {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
  border: 2px solid #fca5a5;
}

.error-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.error-header i {
  color: #dc2626;
  font-size: 1.5rem;
}

.error-header h2 {
  color: #dc2626;
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.error-message {
  margin: 1rem 0;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #7f1d1d;
  border-left: 4px solid #dc2626;
}

.retry-button {
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition:
    background 0.2s ease,
    transform 0.1s ease;
}

.retry-button:hover {
  background: #b91c1c;
  transform: translateY(-1px);
}

.retry-button:active {
  transform: translateY(0);
}

.retry-button:focus-visible {
  outline: 2px solid #dc2626;
  outline-offset: 2px;
}

/* Animations & Transitions */
.fade-slide-enter-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-enter-active {
  transition: opacity 0.3s ease;
}

.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .test-view {
    padding: 1rem;
    margin: 1rem auto;
  }

  h1 {
    font-size: 2rem;
  }

  .answer {
    font-size: 3.5rem;
  }

  .answer-box {
    padding: 2rem 1rem;
  }

  .info-box,
  .loading-box,
  .error-box {
    padding: 1.5rem;
  }

  .info-item {
    flex-direction: column;
    gap: 0.25rem;
  }
}

/* Accessibility - Focus States */
button:focus-visible,
a:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
  border-radius: 4px;
}

/* Print Styles */
@media print {
  .answer-box,
  .info-box {
    box-shadow: none;
    border: 1px solid #ddd;
  }
}
</style>
